// /scripts — Video Script Studio for Let Go Wellness.
// Pick a clinician, paste a question + theme, generate a clinical, on-brand
// YouTube Shorts / Reels script via the Emergent universal LLM key. Every
// script is persisted in MongoDB so the team builds a library over time.
//
// Admin-only — the underlying endpoints sit behind require_admin on the API.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Trash2,
  Search,
  Mic,
  Film,
  Quote,
  Tag,
  Clock,
  Wand2,
  Lock,
  LogOut,
  Download,
  Share2,
  Package,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_STORAGE_KEY = "lgw_studio_pass";

// Suggested theme chips — quick-pick for the host
const THEME_CHIPS = [
  "Trying to keep up with the world",
  "Anxiety & overwhelm",
  "Burnout & exhaustion",
  "Depression & low motivation",
  "Boundaries & people-pleasing",
  "Sleep & rest",
  "Couples & communication",
  "Parenting under pressure",
  "Grief & loss",
  "Self-worth & identity",
  "Trauma & the body",
  "Life transitions",
];

const LENGTH_OPTIONS = [
  { value: "short", label: "Short", hint: "30–60s · 110–150 words" },
  { value: "medium", label: "Medium", hint: "60–90s · 180–230 words" },
];

export default function VideoScripts() {
  const { site } = useSite();

  // ── Passcode gate ──
  const [pass, setPass] = useState(() => {
    try {
      return localStorage.getItem(PASS_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const headers = useMemo(() => (pass ? { "X-Studio-Pass": pass } : {}), [pass]);
  const signOutStudio = () => {
    try {
      localStorage.removeItem(PASS_STORAGE_KEY);
    } catch (e) { void e; }
    setPass("");
    setLibrary([]);
    setCurrent(null);
  };

  // Build clinician roster from the live team (Waleska + anyone added)
  const clinicians = useMemo(() => {
    const team = Array.isArray(site?.team) ? site.team : [];
    const fromTeam = team
      .filter((m) => m?.name)
      .map((m) => ({ name: m.name, title: m.title || m.spec || "" }));
    if (!fromTeam.length) {
      return [{ name: "Waleska Castillo", title: "LPC · LMT · Founder" }];
    }
    return fromTeam;
  }, [site]);

  // ── Form state ──
  const [clinicianIdx, setClinicianIdx] = useState(0);
  const [theme, setTheme] = useState(THEME_CHIPS[0]);
  const [question, setQuestion] = useState(
    "How do I stop feeling like I'm always behind everyone else?"
  );
  const [length, setLength] = useState("short");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // ── Output + library state ──
  const [current, setCurrent] = useState(null);
  const [library, setLibrary] = useState([]);
  const [libQuery, setLibQuery] = useState("");
  const [libLoading, setLibLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  // Redirect non-admins to login
  // (auth removed — page is public so the studio always works)

  // Load library on mount + after each generation
  const fetchLibrary = async () => {
    if (!pass) return;
    setLibLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/video-scripts`, { headers });
      setLibrary(res.data || []);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        toast.error("Studio passcode rejected. Sign in again.");
        signOutStudio();
      }
    } finally {
      setLibLoading(false);
    }
  };

  useEffect(() => {
    if (pass) fetchLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass]);

  const onGenerate = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!question.trim()) {
      toast.error("Give it a question to answer.");
      return;
    }
    const clinician = clinicians[clinicianIdx] || clinicians[0];
    setBusy(true);
    const t = toast.loading("Writing your script…");
    try {
      const res = await axios.post(
        `${API_BASE}/video-scripts/generate`,
        {
          clinician_name: clinician.name,
          clinician_title: clinician.title,
          theme,
          question,
          length,
          notes,
        },
        { headers }
      );
      setCurrent(res.data);
      toast.success("Script ready — scroll down to read it.", { id: t });
      fetchLibrary();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        toast.error("Studio passcode rejected.", { id: t });
        signOutStudio();
        setBusy(false);
        return;
      }
      const msg = err?.response?.data?.detail || err.message || "Could not generate";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this script from your library?")) return;
    try {
      await axios.delete(`${API_BASE}/video-scripts/${id}`, { headers });
      if (current?.id === id) setCurrent(null);
      fetchLibrary();
      toast.success("Removed from library.");
    } catch (err) {
      console.error(err);
      toast.error("Could not delete.");
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const formatFullBrief = (s) => {
    if (!s) return "";
    return [
      `TITLE: ${s.title_suggestion}`,
      `THUMBNAIL: ${s.thumbnail_text}`,
      `CLINICIAN: ${s.clinician_name}${s.clinician_title ? ` (${s.clinician_title})` : ""}`,
      `THEME: ${s.theme}`,
      `QUESTION: ${s.question}`,
      ``,
      `HOOK:`,
      s.hook,
      ``,
      `SCRIPT:`,
      s.body,
      ``,
      `PULL QUOTE: ${s.pull_quote}`,
      `CTA: ${s.cta}`,
      ``,
      `VIDEO CUES:`,
      ...(s.cues || []).map((c) => `• ${c}`),
    ].join("\n");
  };

  const filteredLibrary = useMemo(() => {
    if (!libQuery.trim()) return library;
    const q = libQuery.toLowerCase();
    return library.filter(
      (s) =>
        (s.question || "").toLowerCase().includes(q) ||
        (s.theme || "").toLowerCase().includes(q) ||
        (s.body || "").toLowerCase().includes(q) ||
        (s.clinician_name || "").toLowerCase().includes(q)
    );
  }, [library, libQuery]);

  // ── Render the passcode gate before anything else ──
  if (!pass) {
    return <StudioGate onUnlock={(v) => {
      try { localStorage.setItem(PASS_STORAGE_KEY, v); } catch (e) { void e; }
      setPass(v);
    }} />;
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="video-scripts-page">
      <Navbar />

      <section className="border-b border-[#E2DCD0] pt-48 pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#9F5C45] hover:text-[#3A5A40]"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </Link>
            <button
              type="button"
              onClick={signOutStudio}
              data-testid="studio-sign-out"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#86827B] hover:text-[#9F5C45]"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
          <p className="overline text-[#9F5C45] mt-6 mb-3">Studio · Video Scripts</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1C1917]">
            Ask a question. <em className="text-[#3A5A40]">Get a script.</em>
          </h1>
          <p className="text-[#57534E] mt-4 max-w-2xl text-sm md:text-base">
            Short clinician-voiced scripts for YouTube Shorts and Instagram Reels. Pick a clinician,
            paste a real audience question, give it a theme — we hand back a hook, the spoken script,
            video cues, a pull-quote and a title. Every script saves automatically below.
          </p>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="py-12 border-b border-[#E2DCD0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <form onSubmit={onGenerate} className="grid lg:grid-cols-[1.1fr_1fr] gap-10">
            {/* Left: question + notes */}
            <div className="space-y-6">
              <div>
                <label className="overline text-[#9F5C45] block mb-2">
                  The question your audience is asking
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={3}
                  placeholder="e.g. How do I stop feeling like I'm always behind everyone else?"
                  data-testid="question-input"
                  className="w-full border border-[#D6CFC2] bg-white px-4 py-3 text-base focus:outline-none focus:border-[#3A5A40] resize-none"
                />
              </div>

              <div>
                <label className="overline text-[#9F5C45] block mb-2">
                  Theme (sets the emotional frame)
                </label>
                <input
                  type="text"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder="e.g. Trying to keep up with the world"
                  data-testid="theme-input"
                  className="w-full border border-[#D6CFC2] bg-white px-4 py-3 text-base focus:outline-none focus:border-[#3A5A40]"
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {THEME_CHIPS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setTheme(chip)}
                      data-testid={`theme-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${
                        theme === chip
                          ? "bg-[#3A5A40] text-white border-[#3A5A40]"
                          : "bg-transparent text-[#3A5A40] border-[#3A5A40]/40 hover:border-[#3A5A40]"
                      }`}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline text-[#9F5C45] block mb-2">
                  Extra direction (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Lean in on EMDR, no clinical jargon, end with permission to rest."
                  data-testid="notes-input"
                  className="w-full border border-[#D6CFC2] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#3A5A40] resize-none"
                />
              </div>
            </div>

            {/* Right: clinician + length + generate button */}
            <div className="space-y-6">
              <div>
                <label className="overline text-[#9F5C45] block mb-2">
                  <Mic className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
                  Who&apos;s on camera?
                </label>
                <div className="space-y-2">
                  {clinicians.map((c, i) => (
                    <button
                      key={`${c.name}-${i}`}
                      type="button"
                      onClick={() => setClinicianIdx(i)}
                      data-testid={`clinician-${i}`}
                      className={`w-full text-left p-4 border transition-colors ${
                        clinicianIdx === i
                          ? "border-[#3A5A40] bg-[#3A5A40]/5"
                          : "border-[#D6CFC2] hover:border-[#3A5A40]"
                      }`}
                    >
                      <p className="font-serif text-lg text-[#1C1917]">{c.name}</p>
                      {c.title && (
                        <p className="text-xs uppercase tracking-[0.22em] text-[#9F5C45] mt-1">
                          {c.title}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#86827B] mt-3">
                  Add more clinicians via Admin → Team and they&apos;ll appear here automatically.
                </p>
              </div>

              <div>
                <label className="overline text-[#9F5C45] block mb-2">
                  <Clock className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" /> Video length
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {LENGTH_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setLength(opt.value)}
                      data-testid={`length-${opt.value}`}
                      className={`text-left p-3 border transition-colors ${
                        length === opt.value
                          ? "border-[#3A5A40] bg-[#3A5A40]/5"
                          : "border-[#D6CFC2] hover:border-[#3A5A40]"
                      }`}
                    >
                      <p className="font-serif text-base text-[#1C1917]">{opt.label}</p>
                      <p className="text-xs text-[#86827B] mt-0.5">{opt.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="generate-script"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#243B2C] text-xs uppercase tracking-[0.22em] disabled:opacity-60 transition-colors"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Writing…
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" /> Generate script
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ── CURRENT OUTPUT ── */}
      {current && (
        <ScriptCard
          script={current}
          onCopy={copy}
          copiedKey={copiedKey}
          fullBriefFn={formatFullBrief}
          headers={headers}
          onUpdate={(updated) => {
            setCurrent(updated);
            setLibrary((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          }}
        />
      )}

      {/* ── LIBRARY ── */}
      <section className="py-16 border-t border-[#E2DCD0] bg-[#F4F1EA]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <p className="overline text-[#9F5C45] mb-2">Library</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
                Every script you&apos;ve written
              </h2>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86827B]" />
              <input
                type="text"
                placeholder="Search by question, theme, or clinician…"
                value={libQuery}
                onChange={(e) => setLibQuery(e.target.value)}
                data-testid="library-search"
                className="border border-[#D6CFC2] bg-white pl-10 pr-4 py-2 text-sm w-full md:w-80 focus:outline-none focus:border-[#3A5A40]"
              />
            </div>
          </div>

          {libLoading ? (
            <div className="text-center py-12 text-[#86827B]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : filteredLibrary.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#D6CFC2] bg-white">
              <p className="text-[#86827B]">No scripts yet — generate your first one above.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredLibrary.map((s) => (
                <LibraryRow
                  key={s.id}
                  script={s}
                  isOpen={current?.id === s.id}
                  onOpen={() => setCurrent(s)}
                  onDelete={() => onDelete(s.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers — download script as .txt and Web Share for mobile
// (Web Share opens the native iOS/Android share sheet, which is
// what lets users send straight into Apple Notes, Google Keep,
// Messages, Mail, Drive, etc.)
// ─────────────────────────────────────────────────────────────
function safeName(s) {
  return (s || "letgo-script")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "letgo-script";
}

function downloadAsTxt(text, filename) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".txt") ? filename : `${filename}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Build a YouTube-ready description from a script (title, pull-quote
// teaser, full transcript, sage/gold-tinted CTA, hashtags drawn from
// the theme + brand). 5000-char limit on YouTube — we stay well under.
function buildYoutubeDescription(s) {
  const title = (s.title_suggestion || s.question || "Let Go Wellness").trim();
  const pull = (s.pull_quote || "").trim();
  const cta = (s.cta || "Visit letgowelllness.com to connect with our team in Waterbury, CT.").trim();
  const themeTag = (s.theme || "").replace(/[^a-zA-Z0-9]+/g, "");
  const clinTag = (s.clinician_name || "").replace(/[^a-zA-Z0-9]+/g, "");

  const tags = [
    "LetGoWellness",
    "MentalHealth",
    "Therapy",
    "WaterburyCT",
    "ConnecticutTherapist",
    themeTag,
    clinTag,
    s.length === "short" ? "Shorts" : "MentalHealthMatters",
  ].filter(Boolean).map((t) => `#${t}`).join(" ");

  return [
    pull ? `"${pull}"` : title,
    "",
    s.body || "",
    "",
    "—",
    cta,
    "",
    "🌿 Let Go Wellness · Waterbury, CT",
    "→ Book online: https://letgowelllness.com",
    "→ Call: 203-437-8193",
    "",
    "Disclaimer: This video is for educational and informational purposes only. It is not a substitute for individualized mental-health care. If you're in crisis, please call or text 988 (US).",
    "",
    tags,
  ].join("\n");
}

async function shareScript(title, text, filename) {
  // Mobile: try Web Share with a real file attached so iOS suggests "Save to Notes"
  if (navigator.canShare && navigator.share && window.File) {
    try {
      const file = new File([text], filename.endsWith(".txt") ? filename : `${filename}.txt`, {
        type: "text/plain",
      });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title, files: [file] });
        return "shared";
      }
    } catch (err) {
      if (err?.name === "AbortError") return "cancelled";
    }
  }
  // Plain text share (most Android + some iOS)
  if (navigator.share) {
    try {
      await navigator.share({ title, text });
      return "shared";
    } catch (err) {
      if (err?.name === "AbortError") return "cancelled";
    }
  }
  // Desktop fallback — just trigger the file download
  downloadAsTxt(text, filename);
  return "downloaded";
}

// ─────────────────────────────────────────────────────────────
function ScriptCard({ script, onCopy, copiedKey, fullBriefFn, headers, onUpdate }) {
  const s = script;
  const [thumbBusy, setThumbBusy] = useState(false);

  const generateThumb = async () => {
    if (thumbBusy) return;
    setThumbBusy(true);
    const t = toast.loading("Painting your thumbnail with Nano Banana…");
    try {
      const res = await axios.post(`${API_BASE}/video-scripts/${s.id}/thumbnail`, {}, { headers });
      onUpdate?.(res.data);
      toast.success("Thumbnail ready — scroll to see it.", { id: t });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Could not generate thumbnail.";
      toast.error(msg, { id: t });
    } finally {
      setThumbBusy(false);
    }
  };

  const downloadThumb = async () => {
    if (!s.thumbnail_url) return;
    try {
      const fullUrl = s.thumbnail_url.startsWith("http")
        ? s.thumbnail_url
        : `${process.env.REACT_APP_BACKEND_URL}${s.thumbnail_url}`;
      const res = await fetch(fullUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `letgo-thumb-${safeName(s.theme || s.title_suggestion || "script")}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Thumbnail downloaded.");
    } catch (err) {
      console.error(err);
      toast.error("Could not download thumbnail.");
    }
  };

  // ── YouTube post pack ── one ZIP containing script.txt, thumbnail.jpg,
  // youtube-description.txt, title.txt and a README. Drop straight into
  // the YouTube uploader and paste the description.
  const [packBusy, setPackBusy] = useState(false);
  const buildPostPack = async () => {
    if (packBusy) return;
    setPackBusy(true);
    const t = toast.loading("Bundling your YouTube post pack…");
    try {
      const zip = new JSZip();
      const baseName = safeName(s.theme || s.title_suggestion || "script");
      const folder = zip.folder(`letgo-youtube-${baseName}`);

      folder.file("title.txt", (s.title_suggestion || s.question || "Let Go Wellness").trim());
      folder.file("description.txt", buildYoutubeDescription(s));
      folder.file(`${baseName}-script.txt`, fullBriefFn(s));

      // Embed the thumbnail if it exists
      if (s.thumbnail_url) {
        const fullUrl = s.thumbnail_url.startsWith("http")
          ? s.thumbnail_url
          : `${process.env.REACT_APP_BACKEND_URL}${s.thumbnail_url}`;
        try {
          const res = await fetch(fullUrl);
          const blob = await res.blob();
          folder.file(`${baseName}-thumbnail.jpg`, blob);
        } catch (e) {
          console.warn("Skipping thumbnail in pack:", e);
        }
      }

      folder.file(
        "README.txt",
        [
          `Let Go Wellness · YouTube post pack`,
          ``,
          `Files`,
          `─────`,
          `• title.txt                   — paste into YouTube's Title field`,
          `• description.txt             — paste into the Description field (includes CTA + hashtags)`,
          `• ${baseName}-thumbnail.jpg   — upload as the video thumbnail (16:9)`,
          `• ${baseName}-script.txt      — full read-aloud script + video cues + pull-quote`,
          ``,
          `Upload checklist`,
          `────────────────`,
          `1. Open YouTube Studio → Upload`,
          `2. Drag in your finished video file`,
          `3. Paste title.txt into Title`,
          `4. Paste description.txt into Description`,
          `5. Upload ${baseName}-thumbnail.jpg as the thumbnail`,
          `6. Set category to "Education" or "Howto & Style"`,
          `7. Toggle "Made for kids" → No (mental-health content)`,
          ``,
          `Generated by the Let Go Wellness Video Script Studio`,
          `https://letgowelllness.com/video`,
        ].join("\n")
      );

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `letgo-youtube-${baseName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Post pack ready — drop the ZIP files into YouTube Studio.", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Could not build the post pack.", { id: t });
    } finally {
      setPackBusy(false);
    }
  };

  return (
    <section className="py-16 border-b border-[#E2DCD0]" data-testid="script-output">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-baseline gap-3 mb-2">
          <Tag className="w-4 h-4 text-[#9F5C45]" />
          <span className="text-xs uppercase tracking-[0.22em] text-[#9F5C45]">{s.theme || "Untitled theme"}</span>
          <span className="text-xs text-[#86827B]">·</span>
          <span className="text-xs text-[#86827B]">{s.clinician_name}</span>
          <span className="text-xs text-[#86827B]">·</span>
          <span className="text-xs text-[#86827B]">{s.length === "short" ? "30–60s" : "60–90s"}</span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#1C1917] mb-2">
          {s.title_suggestion || s.question}
        </h2>
        <p className="text-[#57534E] italic mb-8">{s.question}</p>

        {/* Hook */}
        {s.hook && (
          <Block label="The Hook" icon={<Sparkles className="w-4 h-4" />}>
            <p className="font-serif text-xl text-[#243B2C] leading-snug">{s.hook}</p>
            <CopyBtn text={s.hook} onCopy={onCopy} k={`hook-${s.id}`} copiedKey={copiedKey} />
          </Block>
        )}

        {/* Body */}
        <Block label="The Script" icon={<Mic className="w-4 h-4" />}>
          <pre className="whitespace-pre-wrap font-sans text-base text-[#1C1917] leading-relaxed">
{s.body}
          </pre>
          <CopyBtn text={s.body} onCopy={onCopy} k={`body-${s.id}`} copiedKey={copiedKey} />
        </Block>

        {/* Pull quote */}
        {s.pull_quote && (
          <Block label="Pull Quote" icon={<Quote className="w-4 h-4" />}>
            <p className="font-serif italic text-2xl text-[#3A5A40] leading-snug">
              &ldquo;{s.pull_quote}&rdquo;
            </p>
            <CopyBtn text={s.pull_quote} onCopy={onCopy} k={`pq-${s.id}`} copiedKey={copiedKey} />
          </Block>
        )}

        {/* Cues */}
        {!!(s.cues && s.cues.length) && (
          <Block label="Video Cues" icon={<Film className="w-4 h-4" />}>
            <ul className="space-y-2">
              {s.cues.map((c, i) => (
                <li key={i} className="flex gap-3 text-[#1C1917]">
                  <span className="text-[#9F5C45] font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <CopyBtn
              text={s.cues.map((c, i) => `${i + 1}. ${c}`).join("\n")}
              onCopy={onCopy}
              k={`cues-${s.id}`}
              copiedKey={copiedKey}
            />
          </Block>
        )}

        {/* CTA */}
        {s.cta && (
          <Block label="Closing CTA" icon={<Sparkles className="w-4 h-4" />}>
            <p className="text-[#1C1917]">{s.cta}</p>
            <CopyBtn text={s.cta} onCopy={onCopy} k={`cta-${s.id}`} copiedKey={copiedKey} />
          </Block>
        )}

        {/* YouTube meta */}
        <div className="grid md:grid-cols-2 gap-6 mt-8 p-6 border border-[#3A5A40]/30 bg-[#3A5A40]/5">
          <div>
            <p className="overline text-[#9F5C45] mb-1">YouTube title</p>
            <p className="font-serif text-lg text-[#1C1917]">{s.title_suggestion || "—"}</p>
          </div>
          <div>
            <p className="overline text-[#9F5C45] mb-1">Thumbnail text</p>
            <p className="font-serif text-2xl text-[#3A5A40]">{s.thumbnail_text || "—"}</p>
          </div>
        </div>

        {/* Thumbnail (AI generated) */}
        <div className="mt-8" data-testid="thumbnail-block">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <p className="overline text-[#9F5C45] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> AI thumbnail
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={generateThumb}
                disabled={thumbBusy}
                data-testid="generate-thumbnail"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#243B2C] text-xs uppercase tracking-[0.22em] disabled:opacity-60 transition-colors"
              >
                {thumbBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Painting…
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" /> {s.thumbnail_url ? "Re-generate" : "Generate thumbnail"}
                  </>
                )}
              </button>
              {s.thumbnail_url && (
                <button
                  type="button"
                  onClick={downloadThumb}
                  data-testid="download-thumbnail"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white text-xs uppercase tracking-[0.22em] transition-colors"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
              )}
              <button
                type="button"
                onClick={buildPostPack}
                disabled={packBusy}
                data-testid="youtube-post-pack"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#9F5C45] text-[#9F5C45] hover:bg-[#9F5C45] hover:text-white text-xs uppercase tracking-[0.22em] disabled:opacity-60 transition-colors"
                title="Title + description + thumbnail + script — one ZIP ready for YouTube Studio."
              >
                {packBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Bundling…
                  </>
                ) : (
                  <>
                    <Package className="w-4 h-4" /> YouTube post pack
                  </>
                )}
              </button>
            </div>
          </div>
          {s.thumbnail_url ? (
            <div className="border border-[#E2DCD0] bg-[#F4F1EA]">
              <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
                <img
                  src={
                    s.thumbnail_url.startsWith("http")
                      ? s.thumbnail_url
                      : `${process.env.REACT_APP_BACKEND_URL}${s.thumbnail_url}`
                  }
                  alt={s.title_suggestion || "YouTube thumbnail"}
                  data-testid="thumbnail-img"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#D6CFC2] bg-[#F4F1EA] p-8 text-center text-[#86827B] text-sm">
              No thumbnail yet — click <em>Generate thumbnail</em> and we&apos;ll paint a 1280×720 sage &amp; gold image you can upload straight to YouTube.
            </div>
          )}
        </div>

        {/* Export row — copy / download / share to Notes */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => onCopy(fullBriefFn(s), `brief-${s.id}`)}
            data-testid="copy-full-brief"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white text-xs uppercase tracking-[0.22em] transition-colors"
          >
            {copiedKey === `brief-${s.id}` ? (
              <>
                <Check className="w-4 h-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy the whole brief
              </>
            )}
          </button>

          <button
            onClick={() => {
              const filename = `letgo-${safeName(s.theme || s.question)}`;
              downloadAsTxt(fullBriefFn(s), filename);
              toast.success("Script saved as .txt — open it in any notes app.");
            }}
            data-testid="download-brief-txt"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white text-xs uppercase tracking-[0.22em] transition-colors"
          >
            <Download className="w-4 h-4" /> Download .txt
          </button>

          <button
            onClick={async () => {
              const filename = `letgo-${safeName(s.theme || s.question)}`;
              const title = s.title_suggestion || s.question || "Let Go Wellness script";
              const result = await shareScript(title, fullBriefFn(s), filename);
              if (result === "downloaded") {
                toast.success("Sharing not supported here — downloaded the .txt instead.");
              } else if (result === "shared") {
                toast.success("Sent — pick Notes from the share sheet to save it.");
              }
            }}
            data-testid="share-brief"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#243B2C] text-xs uppercase tracking-[0.22em] transition-colors"
          >
            <Share2 className="w-4 h-4" /> Send to Notes
          </button>
        </div>
      </div>
    </section>
  );
}

function Block({ label, icon, children }) {
  return (
    <div className="mb-6 relative">
      <div className="flex items-center gap-2 mb-2 text-[#9F5C45]">
        {icon}
        <span className="overline">{label}</span>
      </div>
      <div className="border border-[#E2DCD0] bg-white p-5 pr-14">{children}</div>
    </div>
  );
}

function CopyBtn({ text, onCopy, k, copiedKey }) {
  return (
    <button
      onClick={() => onCopy(text, k)}
      data-testid={`copy-${k}`}
      className="absolute top-9 right-3 p-2 text-[#86827B] hover:text-[#3A5A40] transition-colors"
      title="Copy"
      type="button"
    >
      {copiedKey === k ? <Check className="w-4 h-4 text-[#3A5A40]" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

function LibraryRow({ script, isOpen, onOpen, onDelete }) {
  return (
    <div
      className={`border bg-white p-5 transition-colors ${
        isOpen ? "border-[#3A5A40]" : "border-[#E2DCD0]"
      }`}
      data-testid={`library-row-${script.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <button onClick={onOpen} className="text-left flex-1" type="button">
          <p className="text-xs uppercase tracking-[0.22em] text-[#9F5C45] mb-1">
            {script.theme || "Untitled theme"}
          </p>
          <p className="font-serif text-lg text-[#1C1917] line-clamp-2">
            {script.title_suggestion || script.question}
          </p>
          <p className="text-xs text-[#86827B] mt-2">
            {script.clinician_name} · {script.length === "short" ? "30–60s" : "60–90s"} ·{" "}
            {new Date(script.created_at).toLocaleDateString()}
          </p>
        </button>
        <button
          onClick={onDelete}
          data-testid={`delete-${script.id}`}
          className="p-2 text-[#86827B] hover:text-[#9F5C45]"
          title="Delete"
          type="button"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Studio Gate — single passcode field. Lightweight, no real auth,
// just stops random visitors from burning the LLM budget.
// ─────────────────────────────────────────────────────────────
function StudioGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSubmitting(true);
    // Verify against the backend so an invalid passcode fails fast
    try {
      const res = await fetch(`${API_BASE}/video-scripts`, {
        headers: { "X-Studio-Pass": value.trim() },
      });
      if (res.status === 401) {
        toast.error("Wrong passcode — try again.");
        setSubmitting(false);
        return;
      }
      onUnlock(value.trim());
    } catch {
      // Network blip — let them in optimistically; the next API call will catch it
      onUnlock(value.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased flex items-center justify-center px-6" data-testid="studio-gate">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Film className="w-8 h-8 text-[#3A5A40] mx-auto mb-4" />
          <p className="overline text-[#9F5C45] mb-3">Studio · Video Scripts</p>
          <h1 className="font-serif font-light text-4xl tracking-tight">Enter the studio.</h1>
          <p className="text-[#57534E] mt-3 text-sm">
            One passcode unlocks the script writer. You&apos;ll stay signed in on this device.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E2DCD0] p-8 space-y-6" data-testid="studio-gate-form">
          <label className="block">
            <span className="overline text-[#57534E] mb-2 block">Studio passcode</span>
            <input
              data-testid="studio-passcode-input"
              type="password"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="••••••••"
              className="w-full border-0 border-b-2 border-[#D6CFC2] bg-transparent px-1 py-3 text-base focus:outline-none focus:border-[#3A5A40]"
            />
          </label>
          <button
            type="submit"
            disabled={submitting || !value}
            data-testid="studio-passcode-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#243B2C] text-xs uppercase tracking-[0.22em] disabled:opacity-60 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Checking
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" /> Enter studio
              </>
            )}
          </button>
        </form>

        <p className="text-center text-xs text-[#86827B] mt-6">
          <Link to="/" className="hover:text-[#3A5A40]">← Back to site</Link>
        </p>
      </div>
    </main>
  );
}

